import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Share2, Download, Hexagon, ShieldCheck, Users, Coins, Flame, Calendar, DollarSign, Trophy, ArrowLeft, Copy, Check } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

export default function ShareImpactPage({ onBack }) {
  const { token, API, user } = useAuth();
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);

  const fetchCardData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/share/card-data`, { headers: { Authorization: `Bearer ${token}` } });
      setCardData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API, token]);

  useEffect(() => { fetchCardData(); }, [fetchCardData]);

  const generateCanvas = useCallback(() => {
    if (!cardData || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const w = 600;
    const h = 400;
    canvas.width = w;
    canvas.height = h;

    // Background
    ctx.fillStyle = '#09090B';
    ctx.fillRect(0, 0, w, h);

    // Subtle grid pattern
    ctx.strokeStyle = 'rgba(245, 158, 11, 0.05)';
    ctx.lineWidth = 0.5;
    for (let x = 0; x < w; x += 30) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    for (let y = 0; y < h; y += 30) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }

    // Top accent line
    const grad = ctx.createLinearGradient(0, 0, w, 0);
    grad.addColorStop(0, '#F59E0B');
    grad.addColorStop(1, '#D97706');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, 4);

    // Logo area
    ctx.fillStyle = '#F59E0B';
    ctx.font = 'bold 14px sans-serif';
    ctx.fillText('HONEY POT', 32, 40);

    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.font = '10px sans-serif';
    ctx.fillText('Protecting the Creator Economy', 32, 56);

    // Name
    ctx.fillStyle = '#FAFAFA';
    ctx.font = 'bold 28px sans-serif';
    ctx.fillText(cardData.name + "'s Impact", 32, 100);

    // Stats grid
    const stats = [
      { label: 'Links Protected', value: String(cardData.links_protected), color: '#10B981' },
      { label: 'Creators Supported', value: String(cardData.creators_supported), color: '#3B82F6' },
      { label: 'Points Earned', value: String(cardData.points), color: '#F59E0B' },
      { label: 'Level', value: String(cardData.level), color: '#8B5CF6' },
      { label: 'Day Streak', value: String(cardData.daily_streak), color: '#EF4444' },
      { label: 'Revenue Saved', value: '$' + cardData.estimated_revenue_saved, color: '#10B981' },
    ];

    const cols = 3;
    const cardW = 170;
    const cardH = 80;
    const startX = 32;
    const startY = 130;
    const gap = 12;

    stats.forEach((stat, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * (cardW + gap);
      const y = startY + row * (cardH + gap);

      // Card bg
      ctx.fillStyle = 'rgba(255,255,255,0.03)';
      ctx.beginPath();
      ctx.roundRect(x, y, cardW, cardH, 8);
      ctx.fill();

      // Border
      ctx.strokeStyle = 'rgba(255,255,255,0.06)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.roundRect(x, y, cardW, cardH, 8);
      ctx.stroke();

      // Value
      ctx.fillStyle = stat.color;
      ctx.font = 'bold 24px sans-serif';
      ctx.fillText(stat.value, x + 14, y + 38);

      // Label
      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.font = '11px sans-serif';
      ctx.fillText(stat.label, x + 14, y + 58);
    });

    // Top creators
    if (cardData.top_creators.length > 0) {
      const ty = startY + 2 * (cardH + gap) + 20;
      ctx.fillStyle = 'rgba(255,255,255,0.3)';
      ctx.font = '10px sans-serif';
      ctx.fillText('TOP CREATORS SUPPORTED', 32, ty);

      ctx.fillStyle = '#FAFAFA';
      ctx.font = '12px sans-serif';
      const creatorsText = cardData.top_creators.map(c => c.name).join('  ·  ');
      ctx.fillText(creatorsText, 32, ty + 18);
    }

    // Footer
    ctx.fillStyle = 'rgba(245, 158, 11, 0.15)';
    ctx.fillRect(0, h - 36, w, 36);
    ctx.fillStyle = '#F59E0B';
    ctx.font = 'bold 10px sans-serif';
    ctx.fillText('honeypot.app', 32, h - 14);
    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.font = '10px sans-serif';
    ctx.fillText('Join the movement. Protect creators.', w - 240, h - 14);
  }, [cardData]);

  useEffect(() => {
    if (cardData) {
      setTimeout(generateCanvas, 100);
    }
  }, [cardData, generateCanvas]);

  const downloadCard = () => {
    if (!canvasRef.current) return;
    const link = document.createElement('a');
    link.download = `honeypot-impact-${user?.name?.replace(/\s/g, '-')}.png`;
    link.href = canvasRef.current.toDataURL('image/png');
    link.click();
  };

  const copyStats = () => {
    if (!cardData) return;
    const text = `My Honey Pot Impact:\n` +
      `${cardData.links_protected} links protected\n` +
      `${cardData.creators_supported} creators supported\n` +
      `${cardData.points} points earned (Level ${cardData.level})\n` +
      `$${cardData.estimated_revenue_saved} estimated revenue saved for creators\n\n` +
      `Join me in protecting the creator economy! #HoneyPot`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="p-5 flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-5 space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          data-testid="share-back-btn"
          onClick={onBack}
          className="p-1.5 rounded-lg text-zinc-500 hover:text-white hover:bg-white/5 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h2 className="font-heading font-bold text-lg text-white">Share Your Impact</h2>
          <p className="text-[10px] text-zinc-500">Show the world how you support creators</p>
        </div>
      </div>

      {/* Canvas Card Preview */}
      <div className="glass-card rounded-xl p-3 overflow-hidden">
        <canvas
          ref={canvasRef}
          className="w-full rounded-lg"
          style={{ aspectRatio: '600/400' }}
          data-testid="share-card-canvas"
        />
      </div>

      {/* Quick Stats */}
      {cardData && (
        <div className="grid grid-cols-3 gap-2">
          <div className="glass-card rounded-lg p-3 text-center">
            <ShieldCheck className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
            <p className="font-mono font-bold text-sm text-white">{cardData.links_protected}</p>
            <p className="text-[9px] text-zinc-500">Links</p>
          </div>
          <div className="glass-card rounded-lg p-3 text-center">
            <Users className="w-4 h-4 text-blue-400 mx-auto mb-1" />
            <p className="font-mono font-bold text-sm text-white">{cardData.creators_supported}</p>
            <p className="text-[9px] text-zinc-500">Creators</p>
          </div>
          <div className="glass-card rounded-lg p-3 text-center">
            <DollarSign className="w-4 h-4 text-green-400 mx-auto mb-1" />
            <p className="font-mono font-bold text-sm text-white">${cardData.estimated_revenue_saved}</p>
            <p className="text-[9px] text-zinc-500">Saved</p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="space-y-2">
        <button
          data-testid="share-download-btn"
          onClick={downloadCard}
          className="honey-btn w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
        >
          <Download className="w-4 h-4" />
          Download Impact Card
        </button>
        <button
          data-testid="share-copy-btn"
          onClick={copyStats}
          className="w-full py-3 rounded-xl text-sm font-medium glass-card flex items-center justify-center gap-2 hover:border-amber-500/20 transition-all"
        >
          {copied ? (
            <>
              <Check className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400">Copied to clipboard!</span>
            </>
          ) : (
            <>
              <Copy className="w-4 h-4 text-zinc-400" />
              <span className="text-zinc-300">Copy Stats as Text</span>
            </>
          )}
        </button>
      </div>

      {/* Top Creators */}
      {cardData?.top_creators?.length > 0 && (
        <div>
          <p className="text-xs font-bold text-zinc-400 mb-2">Your Top Creators</p>
          <div className="space-y-1.5">
            {cardData.top_creators.map((c, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.02]">
                <div className="w-6 h-6 rounded-md bg-amber-500/10 flex items-center justify-center">
                  <Trophy className={`w-3 h-3 ${i === 0 ? 'text-amber-500' : i === 1 ? 'text-zinc-400' : 'text-amber-800'}`} />
                </div>
                <span className="text-xs text-white flex-1">{c.name}</span>
                <span className="text-[10px] font-mono text-zinc-500">{c.count} links</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
